import type { TFunction } from "i18next";
import { generatePath } from "react-router";

import {
  SchemaDefinition,
  SchemaRelation,
  TableTypeEnum,
} from "shared/api/schemaDefinitions/types";
import { SmartGroup } from "shared/api/smartGroups/api";
import { isSchemaAnalyticsConfigured } from "shared/schemaDefinitions/ByAssetAge/utils";
import { DATETIME_TYPES } from "shared/schemaDefinitions/constants";
import { DataType } from "shared/types";
import { getValueByAccessor } from "shared/utils";
import { formatValue } from "shared/utils/formatValue/formatValue";
import {
  RawTableCellValue,
  TableCellValue,
} from "shared/utils/formatValue/types";

import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator, FilterType } from "features/ui/Filters/types";
import Link from "features/ui/Link";
import { SchemaEntry } from "features/ui/MaterialTable";

import { routes } from "services/routes";

import {
  MATCHED_EVENT_COUNTS_KEY,
  SMART_GROUPS_DEFAULT_FORMAT,
  SMART_GROUPS_KEY,
} from "./constants";

// Convert UTF-8 string to base64 safely
// btoa() only works with Latin1 characters, so we need to encode UTF-8 properly
const utf8ToBase64 = (str: string): string => {
  // Use TextEncoder to convert UTF-8 string to bytes
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);

  // Convert bytes to binary string
  let binaryString = "";
  for (let i = 0; i < bytes.length; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }

  // Now we can safely use btoa on the binary string
  return btoa(binaryString);
};

// Convert base64 to UTF-8 string safely
const base64ToUtf8 = (base64: string): string => {
  // First decode base64 to binary string
  const binaryString = atob(base64);

  // Convert binary string to byte array
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Use TextDecoder to convert bytes to UTF-8 string
  const decoder = new TextDecoder();

  return decoder.decode(bytes);
};

export const encodePrimaryKeys = (primaryKeys: (string | number)[]) =>
  utf8ToBase64(JSON.stringify(primaryKeys))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

export const decodePrimaryKeys = (encoded: string): (string | number)[] => {
  // Restore URL-safe base64 to standard base64
  const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");

  // Decode and parse JSON
  return JSON.parse(base64ToUtf8(base64));
};

// Datetime primary keys are encoded as unix-epoch-milliseconds so the id matches the API and
// platform-services (both use the absolute instant, not a formatted string). The API serves
// datetime as a naive UTC wall-clock string (no offset), so append 'Z' to parse it as UTC.
const datetimePkToEpochMillis = (value: string): string | number => {
  const ms = Date.parse(value.endsWith("Z") ? value : `${value}Z`);

  return Number.isNaN(ms) ? value : ms;
};

const getPrimaryKeyValues = (schema: SchemaDefinition, row: any): any[] =>
  schema.columns.filter((c) => c.isPrimaryKey).map((c) => row[c.name]);

// Only the encoded id represents a datetime PK as epoch-millis; the raw values (and the pretty
// label below) keep the original datetime string, so the epoch number is never shown to a user.
const getEncodedPrimaryKeyValues = (
  schema: SchemaDefinition,
  row: any
): (string | number)[] =>
  schema.columns
    .filter((c) => c.isPrimaryKey)
    .map((c) =>
      DATETIME_TYPES.includes(c.dataType) && typeof row[c.name] === "string"
        ? datetimePkToEpochMillis(row[c.name])
        : row[c.name]
    );

const hasUsablePrimaryKey = (primaryKeyValues: unknown[]) =>
  primaryKeyValues.length > 0 &&
  primaryKeyValues.every((value) => value != null && value !== "");

export const getEncodedSchemaRecordRowId = (
  schema: SchemaDefinition,
  row: any
) => {
  const primaryKeyValues = getEncodedPrimaryKeyValues(schema, row);

  // A blank/absent primary key encodes to a non-unique id (e.g. [""]), which
  // collides across rows and makes MUI row lookups throw. Fall back to the full
  // row so each row still gets a unique, stable grid id.
  return encodePrimaryKeys(
    hasUsablePrimaryKey(primaryKeyValues)
      ? primaryKeyValues
      : [JSON.stringify(row)]
  );
};

export const getPrettySchemaRecordRowId = (
  schema: SchemaDefinition,
  row: any,
  joinWith = ", "
) => getPrimaryKeyValues(schema, row).join(joinWith);

/**
 * Encoded record ID of a related record embedded on the row (the API attaches
 * the many-to-one related row under the relation key). Null when the record is
 * missing or any of its primary key values is blank.
 */
const getEmbeddedRecordEncodedId = (
  relatedSchema: SchemaDefinition,
  relatedRecord: unknown
): string | null => {
  if (
    relatedRecord == null ||
    typeof relatedRecord !== "object" ||
    Array.isArray(relatedRecord)
  ) {
    return null;
  }

  const primaryKeyValues = getEncodedPrimaryKeyValues(
    relatedSchema,
    relatedRecord
  );

  return hasUsablePrimaryKey(primaryKeyValues)
    ? encodePrimaryKeys(primaryKeyValues)
    : null;
};

/**
 * Encoded record ID of the related row a many-to-one relation points at, built
 * from the row's foreign-key values mapped onto the related columns. Null when
 * those values do not cover the related primary key.
 */
const getRelatedRecordEncodedId = (
  relation: SchemaRelation,
  row: any
): string | null => {
  const { relatedSchema, columnPairs } = relation;

  if (!relatedSchema) return null;

  const relatedRecord: Record<string, unknown> = {};

  for (const pair of columnPairs) {
    const relatedColumn = relatedSchema.columns.find(
      (c) => c.ID === pair.relatedColumnID
    );

    if (relatedColumn && pair.column) {
      relatedRecord[relatedColumn.name] = row[pair.column.name];
    }
  }

  return getEmbeddedRecordEncodedId(relatedSchema, relatedRecord);
};

export interface SpreadRelationLinkTarget {
  relatedSchema: SchemaDefinition;
  recordId: string;
  value: TableCellValue;
}

export const getSchemaHomeRoute = (schemaDefinition: SchemaDefinition) =>
  generatePath(
    schemaDefinition.tableType === TableTypeEnum.EVENT &&
      isSchemaAnalyticsConfigured(schemaDefinition)
      ? routes.schemaEventAnalytics
      : routes.schemaRecords,
    {
      tableType: schemaDefinition.tableType,
      schemaName: schemaDefinition.name,
    }
  );

/**
 * Resolves a spread-out many-to-one relation column (accessor
 * "<relationKey>.<column>") to the related record it should link to. Non-null
 * only when the column is a primary key of the related schema or one of the
 * relation's related-side key columns, and the row carries the related record
 * with its primary key set.
 */
export const getSpreadRelationLinkTarget = (
  schemaDefinition: SchemaDefinition,
  field: string,
  row: any
): SpreadRelationLinkTarget | null => {
  const dotIndex = field.indexOf(".");

  if (dotIndex === -1) return null;

  const relationKey = field.slice(0, dotIndex);
  const relatedFieldName = field.slice(dotIndex + 1);
  const relation = schemaDefinition.relations?.find(
    (r) => !r.isOneToMany && r.key === relationKey
  );
  const relatedSchema = relation?.relatedSchema;

  if (!relation || !relatedSchema) return null;

  const relatedColumn = relatedSchema.columns.find(
    (c) => c.name === relatedFieldName
  );
  const isLinkColumn =
    relatedColumn !== undefined &&
    (relatedColumn.isPrimaryKey ||
      relation.columnPairs.some((p) => p.relatedColumnID === relatedColumn.ID));
  const relatedRecord = row[relationKey];
  const recordId = isLinkColumn
    ? getEmbeddedRecordEncodedId(relatedSchema, relatedRecord)
    : null;

  if (recordId === null || relatedRecord[relatedFieldName] == null) {
    return null;
  }

  return {
    relatedSchema,
    recordId,
    value: relatedRecord[relatedFieldName],
  };
};

const renderRecordLink = ({
  schemaDef,
  recordId,
  children,
  elementKey,
}: {
  schemaDef: SchemaDefinition;
  recordId: string;
  children: React.ReactNode;
  elementKey?: string;
}) => (
  <Link
    key={elementKey}
    to={generatePath(routes.schemaRecord, {
      tableType: schemaDef.tableType,
      schemaName: schemaDef.name,
      id: recordId,
    })}
    // Keep the click from bubbling to the row, whose own click handler may
    // open the record quickview.
    onClick={(e) => e.stopPropagation()}
  >
    {children}
  </Link>
);

const formatByEntry = (
  value: RawTableCellValue,
  entry?: SchemaEntry,
  textOnly = false
) =>
  formatValue({
    value,
    dataType: entry?.dataType ?? DataType.STRING,
    format: entry?.format,
    textOnly,
  });

/**
 * Custom content for a schema record field: "Related <X>" match counts and
 * links for relation / foreign-key / spread-relation columns. Null when the
 * field needs no custom rendering, so callers fall back to plain formatting.
 */
export const getSchemaCellContent = ({
  schemaDefinition,
  field,
  row,
  value,
  resolveSchemaEntry,
  handleOverflow = false,
}: {
  schemaDefinition: SchemaDefinition;
  field: string;
  row: Record<string, any>;
  value: unknown;
  resolveSchemaEntry: () => SchemaEntry | undefined;
  handleOverflow?: boolean;
}): JSX.Element | null => {
  // Synthetic "Related <X>" count columns read from the row's matchedEventCounts
  // map. A missing entry means zero matches — render 0 (handled before the null
  // guard, since the dot-accessor value is undefined for absent keys).
  if (field.startsWith(`${MATCHED_EVENT_COUNTS_KEY}.`)) {
    const key = field.slice(MATCHED_EVENT_COUNTS_KEY.length + 1);
    const counts = row[MATCHED_EVENT_COUNTS_KEY] as
      Record<string, number> | undefined;

    return <>{counts?.[key] ?? 0}</>;
  }

  // Spread-out many-to-one relation columns (accessor "<relationKey>.<column>").
  // Resolved before the null guard — the grid has no valueGetter for dotted
  // fields, so `value` is undefined here. Primary key and relation key columns
  // of the related record link to its detail page.
  if (field.includes(".")) {
    const target = getSpreadRelationLinkTarget(schemaDefinition, field, row);

    if (!target) return null;

    return renderRecordLink({
      schemaDef: target.relatedSchema,
      recordId: target.recordId,
      children: formatByEntry(target.value, resolveSchemaEntry()),
    });
  }

  if (value == null) {
    return null;
  }

  if (field === SMART_GROUPS_KEY && Array.isArray(value) && value.length > 0) {
    const entry = resolveSchemaEntry();

    return (
      <>
        {formatValue({
          value: (value as SmartGroup[]).map(({ name }) => name),
          dataType: DataType.STRING,
          format: entry?.format ?? SMART_GROUPS_DEFAULT_FORMAT,
          handleOverflow,
        })}
      </>
    );
  }

  const relation = schemaDefinition.relations?.find((r) => r.key === field);

  if (relation) {
    const { relatedSchema } = relation;

    if (typeof value === "object" && !Array.isArray(value) && relatedSchema) {
      return renderRecordLink({
        schemaDef: relatedSchema,
        recordId: getEncodedSchemaRecordRowId(relatedSchema, value),
        children: getPrettySchemaRecordRowId(relatedSchema, value),
      });
    }

    if (Array.isArray(value) && relatedSchema) {
      const links = value.map((v) => {
        const primaryKeyEncoded = getEncodedSchemaRecordRowId(relatedSchema, v);

        return renderRecordLink({
          schemaDef: relatedSchema,
          recordId: primaryKeyEncoded,
          children: getPrettySchemaRecordRowId(relatedSchema, v, "-"),
          elementKey: primaryKeyEncoded,
        });
      });

      return (
        <div className="truncate w-full">
          {links.reduce((acc, link, index) => {
            if (index === 0) return [link];

            return [
              ...acc,
              <span key={`sep-${index}`} style={{ whiteSpace: "pre" }}>
                {"  , "}
              </span>,
              link,
            ];
          }, [] as React.ReactNode[])}
        </div>
      );
    }
  }

  // A column can be the key of several relations; link to the first related
  // record that resolves. The embedded related record carries the related
  // primary key even when the relation joins on other columns. Mapping the
  // foreign key onto the primary key is the fallback for rows the API attached
  // no related record to.
  const fkRelations = (schemaDefinition.relations ?? []).filter(
    (r) => !r.isOneToMany && r.columnPairs.some((p) => p.column?.name === field)
  );

  for (const fkRelation of fkRelations) {
    const { relatedSchema } = fkRelation;

    if (!relatedSchema) continue;

    const recordId =
      getEmbeddedRecordEncodedId(relatedSchema, row[fkRelation.key]) ??
      getRelatedRecordEncodedId(fkRelation, row);

    if (recordId) {
      return renderRecordLink({
        schemaDef: relatedSchema,
        recordId,
        children: formatByEntry(value as TableCellValue, resolveSchemaEntry()),
      });
    }
  }

  return null;
};

export const getAttributeValue = (
  attribute: string,
  item: Record<string, any>,
  getSchemaForAccessor: (accessor: string) => SchemaEntry<string> | undefined,
  t: TFunction,
  schemaDefinition?: SchemaDefinition,
  textOnly = false
) => {
  const attributeSchema = getSchemaForAccessor(attribute);
  const itemValue = getValueByAccessor(attribute, item);

  const cellContent = schemaDefinition
    ? getSchemaCellContent({
        schemaDefinition,
        field: attribute,
        row: item,
        value: itemValue,
        resolveSchemaEntry: () => attributeSchema,
      })
    : null;

  if (cellContent) {
    return cellContent;
  }

  // An empty list reads as N/A, like null. A JSON column keeps its `[]`, because that is a value.
  if (
    itemValue == null ||
    (Array.isArray(itemValue) &&
      itemValue.length === 0 &&
      attributeSchema?.dataType !== DataType.OBJECT)
  ) {
    return t("common:not_available");
  }

  return formatByEntry(itemValue, attributeSchema, textOnly);
};

export const getSchemaFilter = (
  tableType: string,
  schemaName: string
): string => {
  const typeFilter: FilterRowState = {
    type: "row",
    id: "row-0",
    attribute: "tableType",
    operator: FilterOperator.EQUALS,
    values: [tableType],
  };
  const nameFilter: FilterRowState = {
    type: "row",
    id: "row-1",
    attribute: "name",
    operator: FilterOperator.EQUALS,
    values: [schemaName],
  };
  const group: FilterGroupState = {
    type: "group",
    id: "group-0",
    anyAll: "all",
    children: [typeFilter, nameFilter],
  };

  return getFiltersQuery(group);
};

/**
 * Filter string selecting all EVENT schema definitions — used to populate the
 * related-types picker and to resolve event-type names to display names.
 */
export const eventSchemaListFilter = (): string =>
  getFiltersQuery({
    type: "group",
    id: "group-0",
    anyAll: "all",
    children: [
      {
        type: "row",
        id: "row-0",
        attribute: "tableType",
        operator: FilterOperator.EQUALS,
        values: [TableTypeEnum.EVENT],
      } as FilterRowState,
    ],
  } as FilterGroupState);

/**
 * Builds one NUMBER column per configured related event type. Each column reads
 * `matchedEventCounts.<name>` (already present on every event-listing row) and is
 * sortable/filterable on that same field — the v2 API supports drilling into the
 * JSON map via JSONExtractInt. Mirrors the v1 ClaimsTable.createMatchedCountsSchema
 * pattern.
 */
export const buildMatchedEventCountColumns = (
  matchedTypes: string[],
  displayNameByName: Map<string, string>,
  t: TFunction
): SchemaEntry[] =>
  matchedTypes.map((name) => {
    const label = t("analytics.related", {
      name: displayNameByName.get(name) ?? name,
    });
    const accessor = `${MATCHED_EVENT_COUNTS_KEY}.${name}`;

    return {
      label,
      accessor,
      dataType: DataType.NUMBER,
      sortable: true,
      sort: { fieldNameForAPI: accessor },
      filter: {
        label,
        fieldName: accessor,
        fieldNameForAPI: accessor,
        filterType: FilterType.NUMBER,
        disableSelectFilters: true,
        enableMinMaxFilters: false,
        onlyAllowPositiveIntegers: true,
        isCount: true,
      },
    };
  });

/**
 * Appends the configured "Related <X>" count columns to a listing schema,
 * dropping the bare `matchedEventCounts` object column (present only when the
 * synthetic attribute is enabled). Shared by the records / analytics / dashboard
 * listings so the merge lives in one place.
 */
export const appendMatchedCountColumns = (
  baseSchema: SchemaEntry[],
  matchedColumns: SchemaEntry[]
): SchemaEntry[] => [
  ...baseSchema.filter((entry) => entry.accessor !== MATCHED_EVENT_COUNTS_KEY),
  ...matchedColumns,
];

export const applyConfiguredTableColumns = (
  schema: SchemaEntry[],
  columnAccessors: string[] | undefined
): SchemaEntry[] => {
  if (!columnAccessors || columnAccessors.length === 0) return schema;

  const byAccessor = new Map(schema.map((entry) => [entry.accessor, entry]));

  const shown = columnAccessors
    .map((accessor) => byAccessor.get(accessor))
    .filter(
      (entry): entry is SchemaEntry => entry !== undefined && !entry.hideInTable
    );

  const shownAccessors = new Set(shown.map((entry) => entry.accessor));

  const rest = schema
    .filter((entry) => !shownAccessors.has(entry.accessor))
    .map((entry) =>
      entry.hideInTable ? entry : { ...entry, hideInTable: true }
    );

  return [...shown, ...rest];
};
